import { canvasColors, LOCATION_PIN_ICON_ID } from '../map.constants';
import { isEnvironment } from '@shared/helpers/environment.helpers';
import { Coordinate } from '@shared/models/geography.model';
import {
  BUILDING_SOURCE_ID,
  TERRAIN_SOURCE_ID,
  terrainSource,
} from './map-config.constants';
import { Point2D, PointLike } from '@shared/models/geometry.model';
import {
  toPoint2D,
  projectPointOntoSegmentClamped,
} from '@shared/helpers/geometry/geometry.helpers';
import { GeoJSONSource, Map, MercatorCoordinate } from 'mapbox-gl';
import { SourceId, MapLayerIds } from './map-config.constants';
import {
  MapStoreyGeoJson,
  MapStoreyProperty,
  PointIconId,
} from '../models/map-feature.models';
import { getCoordinateDistance } from '@shared/helpers/geometry/coordinates.helpers';
import {
  getMapStoreyPolygonsFromStoreys,
  getMapStoreyPointsFromStoreys,
  applyGeoJsonToSource,
} from './map-features.helpers';
import { Storey } from '@shared/models/design/storey.model';
import { DraftShape } from '@shared/models/design/storey.model';
import {
  MapHoverState,
  MapSelections,
  PointPayload,
} from '../models/map-observables.models';
import { MapStoreyPointFeature } from '../models/map-feature.models';
import locationPinIconUrl from '@ui/Icon/icons/pin.svg';

const EMPTY_POINT_GEOJSON: MapStoreyGeoJson<MapStoreyPointFeature> = {
  type: 'geojson',
  data: {
    type: 'FeatureCollection',
    features: [],
  },
};

const EMPTY_LOCATION_PIN_GEOJSON: MapStoreyGeoJson<MapStoreyPointFeature> = {
  type: 'geojson',
  data: {
    type: 'FeatureCollection',
    features: [],
  },
};

/**
 * Wait for the map style to load.
 * Returns immediately if style is already loaded, otherwise waits for 'style.load' event.
 * @param map - The Mapbox map instance
 * @returns Promise that resolves when the style is loaded
 */
export const waitForStyleLoad = (map: mapboxgl.Map): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    if (map.isStyleLoaded()) {
      resolve();
      return;
    }
    const onStyleLoad = () => {
      map.off('style.load', onStyleLoad);
      map.off('error', onError);
      resolve();
    };
    const onError = (e: any) => {
      map.off('style.load', onStyleLoad);
      map.off('error', onError);
      reject(e);
    };
    map.once('style.load', onStyleLoad);
    map.once('error', onError);
  });
};

/**
 * Wait for the map to load and remove symbol layers.
 * Returns immediately if map is already loaded, otherwise waits for 'load' event.
 * @param map - The Mapbox map instance
 * @returns Promise that resolves when the map is loaded
 */
export const waitForMapLoad = (map: mapboxgl.Map): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    const onLoad = () => {
      resolve();
    };
    if (map.loaded()) {
      return onLoad();
    }

    const onRemove = () => {
      map.off('load', onLoad);
      map.off('remove', onRemove);
      reject(new Error('Map destroyed before load'));
    };
    map.once('load', onLoad);
    map.once('remove', onRemove);
  });
};

/** Subset of Mapbox used by {@link waitForMapIdle}. */
export type MapIdleHandle = {
  once: (type: 'idle' | 'remove', listener: () => void) => void;
  off: (type: 'idle' | 'remove', listener: () => void) => void;
  triggerRepaint: () => void;
};

/** Why {@link waitForMapIdle} resolved. Print must treat only `'idle'` as painted. */
export type MapIdleReason = 'idle' | 'timeout' | 'removed' | 'cancelled';

type WaitForMapIdleOptions = {
  /** If omitted, waits until idle, map remove, or {@link WaitForMapIdleOptions.signal}. */
  timeoutMs?: number;
  signal?: AbortSignal;
};

/**
 * Resolves on the next Mapbox `idle` (pending tiles/layers have rendered).
 * Triggers a repaint so an already-idle map still emits `idle`.
 * Omit {@link WaitForMapIdleOptions.timeoutMs} to wait until idle/remove/abort —
 * print uses that so a slow attach can still notify; PrintView owns the deadline.
 */
export const waitForMapIdle = (
  map: MapIdleHandle,
  options: WaitForMapIdleOptions = {},
): Promise<MapIdleReason> => {
  return new Promise((resolve) => {
    let settled = false;
    let timeoutId: number | undefined;
    const settle = (reason: MapIdleReason) => {
      if (settled) return;
      settled = true;
      map.off('idle', onIdle);
      map.off('remove', onRemove);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      options.signal?.removeEventListener('abort', onAbort);
      resolve(reason);
    };
    const onIdle = () => settle('idle');
    const onRemove = () => settle('removed');
    const onAbort = () => settle('cancelled');

    if (options.signal?.aborted) {
      settle('cancelled');
      return;
    }
    if (options.timeoutMs !== undefined) {
      timeoutId = window.setTimeout(() => settle('timeout'), options.timeoutMs);
    }
    options.signal?.addEventListener('abort', onAbort, { once: true });
    map.once('idle', onIdle);
    map.once('remove', onRemove);
    map.triggerRepaint();
  });
};

/**
 * Enable terrain on the map.
 * TODO: Before enabling we need to make sure we get line and extrusion in level
 * @param map
 */
export const enableTerrain = (map: mapboxgl.Map) => {
  if (!map.getSource(TERRAIN_SOURCE_ID)) {
    map.addSource(TERRAIN_SOURCE_ID, terrainSource);
  }
  map.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration: 1.0 } as any);
};

/**
 * Remove symbol layers from the map (POI icons, street names, etc.).
 * Preserves custom application layers (Points, DraftPoints, LocationPin).
 * @param map
 */
export const removeSymbolLayers = (map: mapboxgl.Map) => {
  const layers = map.getStyle().layers;
  // Custom layer IDs that should be preserved
  const customLayerIds = new Set([
    MapLayerIds.Points,
    MapLayerIds.DraftPoints,
    MapLayerIds.LocationPin,
  ]);

  for (const layer of layers) {
    // Remove all symbol layers except our custom ones
    if (
      layer.type === 'symbol' &&
      !customLayerIds.has(layer.id as MapLayerIds)
    ) {
      map.removeLayer(layer.id);
    }
  }
};

type CircleIconOptions = {
  strokeColor?: string;
  fillColor?: string;
  id: string;
};

/**
 * Create and load a circle icon into the map for use in symbol layers.
 * The icon is a white circle that can be colored using icon-color paint property.
 * @param map - The Mapbox map instance
 * @param iconId - The ID to use for the icon (default: 'circle-icon')
 * @returns Promise that resolves when the icon is loaded
 */
const loadCircleIcon = async (
  map: mapboxgl.Map,
  {
    strokeColor = canvasColors.lineSelected,
    fillColor = canvasColors.pointFill,
    id,
  }: CircleIconOptions,
): Promise<void> => {
  // Check if icon already exists
  if (map.hasImage(id)) return;

  // Create a canvas element to draw the circle
  const size = 16; // Higher resolution for better quality
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  // Clear canvas with transparent background
  ctx.clearRect(0, 0, size, size);

  // Draw a white circle (will be colored using icon-color)
  const center = size / 2;
  const radius = size / 2 - 4; // Leave padding for halo visibility

  ctx.beginPath();
  ctx.arc(center, center, radius, 0, Math.PI * 2);
  ctx.strokeStyle = strokeColor;
  ctx.stroke();
  ctx.lineWidth = 1;
  ctx.fillStyle = fillColor; // White, will be colored by icon-color
  ctx.fill();

  // Load the image into the map (AI shit, write better code)
  return new Promise((resolve, reject) => {
    map.loadImage(canvas.toDataURL(), (error, image) => {
      if (error) {
        reject(error);
        return;
      }
      if (!image) {
        reject(new Error('Failed to create circle image'));
        return;
      }

      // Prevent duplicate loading (do better code)
      if (!map.hasImage(id)) {
        map.addImage(id, image);
      }
      resolve();
    });
  });
};

const blobToImage = (blob: Blob): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };
    image.src = objectUrl;
  });
};

/** Register the shared location pin image once for the map's current style. */
export const loadLocationPinIcon = async (map: Map): Promise<void> => {
  if (map.hasImage(LOCATION_PIN_ICON_ID)) return;
  const response = await fetch(locationPinIconUrl);
  if (!response.ok) {
    throw new Error('Failed to fetch location pin icon asset');
  }
  const blob = await response.blob();
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(blob);
      map.addImage(LOCATION_PIN_ICON_ID, bitmap);
      bitmap.close?.();
      return;
    } catch (error) {
      if (isEnvironment('development')) {
        console.warn('createImageBitmap failed for location pin icon', error);
      }
    }
  }
  const image = await blobToImage(blob);
  map.addImage(LOCATION_PIN_ICON_ID, image);
};

export const loadMapIcons = async (map: Map): Promise<void[]> => {
  return Promise.all([
    loadCircleIcon(map, {
      id: PointIconId.Default,
      strokeColor: canvasColors.lineSelected,
      fillColor: canvasColors.pointFill,
    }),
    loadCircleIcon(map, {
      id: PointIconId.Selected,
      strokeColor: canvasColors.lineSelected,
      fillColor: canvasColors.lineSelected,
    }),
    loadLocationPinIcon(map),
  ]);
};

/**
 * Convert screen coordinates to geographic coordinates at a specific elevation.
 * Accounts for map pitch and camera position to accurately project coordinates
 * onto a plane at the given elevation using proper perspective projection.
 *
 * Uses ray-casting: casts a ray from the camera through the mouse point and
 * finds where it intersects the horizontal plane at the target elevation.
 * NOTE: THIS IS AN AI BLACKBOX
 */
export const screenToLngLatAtElevation = (
  map: mapboxgl.Map,
  point: PointLike,
  elevation: number,
): Coordinate => {
  const [x, y] = toPoint2D(point);

  // Get ground-level coordinates at the screen point
  const groundPoint = map.unproject([x, y]);

  // If no elevation, use standard unproject
  if (elevation === 0) return groundPoint;

  const pitch = map.getPitch();
  if (pitch === 0) return groundPoint;

  const transform = (map as any).transform;
  if (!transform?.locationPoint) return groundPoint;

  let guess = { ...groundPoint };
  const maxIterations = 10;
  const tolerance = 0.5; // pixels
  const step = 1e-6; // degrees
  const maxDelta = 0.001; // degrees (~100 m)

  const project = (lng: number, lat: number) => {
    const screenPoint = transform.locationPoint({ lng, lat }, elevation);
    if (!screenPoint) return undefined;
    const { x, y } = screenPoint;
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : undefined;
  };

  for (let i = 0; i < maxIterations; i += 1) {
    const base = project(guess.lng, guess.lat);
    if (!base) break;

    const fx = base.x - x;
    const fy = base.y - y;

    if (Math.abs(fx) < tolerance && Math.abs(fy) < tolerance) {
      return guess;
    }

    const lngStep = project(guess.lng + step, guess.lat);
    const latStep = project(guess.lng, guess.lat + step);

    if (!lngStep || !latStep) break;

    const dFx_dLng = (lngStep.x - base.x) / step;
    const dFx_dLat = (latStep.x - base.x) / step;
    const dFy_dLng = (lngStep.y - base.y) / step;
    const dFy_dLat = (latStep.y - base.y) / step;

    const det = dFx_dLng * dFy_dLat - dFx_dLat * dFy_dLng;
    if (!Number.isFinite(det) || Math.abs(det) < 1e-12) break;

    const invDet = 1 / det;
    let deltaLng = invDet * (dFy_dLat * fx - dFx_dLat * fy);
    let deltaLat = invDet * (-dFy_dLng * fx + dFx_dLng * fy);

    if (!Number.isFinite(deltaLng) || !Number.isFinite(deltaLat)) break;

    deltaLng = Math.max(Math.min(deltaLng, maxDelta), -maxDelta);
    deltaLat = Math.max(Math.min(deltaLat, maxDelta), -maxDelta);

    guess = {
      lng: guess.lng - deltaLng,
      lat: Math.max(Math.min(guess.lat - deltaLat, 85), -85),
    };
  }

  return guess;
};

export const projectLngLatAtElevation = (
  map: mapboxgl.Map,
  coordinate: Coordinate,
  elevation: number,
): Point2D => {
  const fallback = (): Point2D =>
    toPoint2D(map.project([coordinate.lng, coordinate.lat], elevation));
  const transform = (
    map as unknown as {
      transform?: {
        width?: number;
        height?: number;
        customLayerMatrix?: () => readonly number[];
      };
    }
  ).transform;
  const matrix = transform?.customLayerMatrix?.();
  if (!matrix || matrix.length < 16) {
    return fallback();
  }
  const canvas = map.getCanvas();
  const width = transform?.width ?? canvas.clientWidth;
  const height = transform?.height ?? canvas.clientHeight;
  if (!(width > 0) || !(height > 0)) return fallback();

  // The storey outlines are custom WebGL geometry. Project their Mercator
  // position with the exact same matrix instead of Map.project(), whose 3D
  // path adds sampled terrain/centre-plane elevation when terrain is enabled.
  const mercator = MercatorCoordinate.fromLngLat(coordinate, elevation);
  const x = mercator.x;
  const y = mercator.y;
  const z = mercator.z;
  const clipX =
    (matrix[0] ?? 0) * x +
    (matrix[4] ?? 0) * y +
    (matrix[8] ?? 0) * z +
    (matrix[12] ?? 0);
  const clipY =
    (matrix[1] ?? 0) * x +
    (matrix[5] ?? 0) * y +
    (matrix[9] ?? 0) * z +
    (matrix[13] ?? 0);
  const clipW =
    (matrix[3] ?? 0) * x +
    (matrix[7] ?? 0) * y +
    (matrix[11] ?? 0) * z +
    (matrix[15] ?? 0);
  if (!Number.isFinite(clipX) || !Number.isFinite(clipY) || !(clipW > 0)) {
    return fallback();
  }

  return [
    ((clipX / clipW + 1) * width) / 2,
    ((1 - clipY / clipW) * height) / 2,
  ];
};

const EARTH_CIRCUMFERENCE_METERS = 40075016.68557849;
const MAPBOX_TILE_SIZE_PX = 512;

export const getMetersPerPixelAtCoordinate = (
  map: mapboxgl.Map,
  coordinate: Coordinate,
): number => {
  const latitudeRadians = (coordinate.lat * Math.PI) / 180;
  const zoom = map.getZoom();
  if (!Number.isFinite(zoom)) return Number.POSITIVE_INFINITY;
  const scale = Math.pow(2, zoom);
  if (!Number.isFinite(scale) || scale <= 0) return Number.POSITIVE_INFINITY;
  return (
    (Math.cos(latitudeRadians) * EARTH_CIRCUMFERENCE_METERS) /
    (MAPBOX_TILE_SIZE_PX * scale)
  );
};

/**
 * Project a point onto a line segment defined by start and end points.
 * Returns the closest point on the segment to the given point.
 */
export const projectPointOntoSegment = (
  point: [number, number],
  start: [number, number],
  end: [number, number],
): [number, number] => {
  const [x, y] = projectPointOntoSegmentClamped(point, start, end);
  return [x, y];
};

export { getShapeRingCoordinates } from '@shared/helpers/design/shape.helpers';

/**
 * Find the best insertion point on a ring for a target coordinate.
 * Returns the insertion index and the projected coordinates.
 */
export const findInsertionPointOnRing = (
  ring: [number, number][],
  target: [number, number],
):
  | {
      insertIndex: number;
      coordinates: [number, number];
    }
  | undefined => {
  if (ring.length < 2) return undefined;
  const uniqueLength = Math.max(ring.length - 1, 1);
  let bestDistance = Number.POSITIVE_INFINITY;
  let bestCoordinates: [number, number] | undefined;
  let bestInsertIndex = -1;
  for (let i = 0; i < ring.length - 1; i += 1) {
    const candidate = projectPointOntoSegment(
      target,
      ring[i] as [number, number],
      ring[i + 1] as [number, number],
    );
    const distance = getCoordinateDistance(candidate, target);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestCoordinates = candidate;
      bestInsertIndex = i + 1;
    }
  }
  if (bestCoordinates === undefined || bestInsertIndex < 0) return undefined;
  const normalizedIndex =
    bestInsertIndex >= uniqueLength ? uniqueLength : bestInsertIndex;
  return { coordinates: bestCoordinates, insertIndex: normalizedIndex };
};

export type ApplyPointMoveOptions = {
  /**
   * Storey levels (MapStoreyProperty.Level) that should receive the update.
   * Defaults to the currently selected storey only.
   */
  levelsToUpdate?: number[];
  /**
   * Optional level to constrain point feature updates to. When omitted, points
   * are updated for all matching features.
   */
  pointLevelOverride?: number;
  /**
   * Insert index overrides per storey level for multi-level inserts.
   */
  levelInsertIndexes?: Record<number, number>;
  /**
   * Shape ids per level when duplicated shapes have distinct identifiers.
   */
  levelShapeIds?: Record<number, string>;
};

/**
 * Apply point move updates to both point and polygon sources.
 */
export const applyPointMove = (
  map: Map,
  payload: PointPayload,
  buildingGeoJson: ReturnType<typeof getMapStoreyPolygonsFromStoreys>,
  options: ApplyPointMoveOptions = {},
): void => {
  const shapeId = payload.shapeId;
  const pointIndex = payload.index;
  if (!shapeId || pointIndex === undefined) return;
  const placeholderId = payload.insertPlaceholderId;
  const ptsSource = map.getSource(SourceId.Points) as GeoJSONSource | undefined;
  if (!ptsSource) return;

  const currentPtsData: any = (ptsSource as any)?._data ?? {
    type: 'FeatureCollection',
    features: [],
  };
  const matchesPointFeature = (ft: any): boolean => {
    if (placeholderId && ft.properties.placeholderId === placeholderId)
      return true;
    if (ft.properties.index !== pointIndex || ft.properties.placeholderId)
      return false;
    const level = ft.properties?.[MapStoreyProperty.Level];
    if (
      options.levelsToUpdate &&
      !options.levelsToUpdate.includes(level as number)
    ) {
      return false;
    }
    const levelShapeId = options.levelShapeIds?.[level];
    if (levelShapeId) return ft.properties.shapeId === levelShapeId;
    return ft.properties.shapeId === shapeId;
  };
  const feature = currentPtsData.features.find((ft: any) => {
    if (!matchesPointFeature(ft)) return false;
    if (options.pointLevelOverride === undefined) return true;
    return (
      ft.properties?.[MapStoreyProperty.Level] === options.pointLevelOverride
    );
  });
  if (!feature?.geometry) return;

  const elevation =
    payload.elevation ?? feature.properties?.[MapStoreyProperty.Elevation] ?? 0;
  const referencePoint =
    payload.screenPoint ??
    map.project([payload.coordinate.lng, payload.coordinate.lat]);
  const coords = screenToLngLatAtElevation(map, referencePoint, elevation);

  feature.geometry.coordinates = [coords.lng, coords.lat];
  ptsSource.setData(currentPtsData);

  const polySource = map.getSource(BUILDING_SOURCE_ID) as
    GeoJSONSource | undefined;
  const polyData: any = (polySource as any)?._data ?? buildingGeoJson.data;
  const levelSet = options.levelsToUpdate
    ? new Set(options.levelsToUpdate)
    : undefined;
  const targetFeatures = polyData.features.filter((feature: any) => {
    const level = feature.properties[MapStoreyProperty.Level];
    const allowedLevel = levelSet
      ? levelSet.has(level)
      : feature.properties[MapStoreyProperty.Selected] === true;
    if (!allowedLevel) return false;
    if (feature?.properties?.id === shapeId) return true;
    const levelShapeId = options.levelShapeIds?.[level];
    return levelShapeId ? feature?.properties?.id === levelShapeId : false;
  });
  if (!targetFeatures.length) return;
  targetFeatures.forEach((feature: any) => {
    const coordsArr = feature?.geometry?.coordinates;
    if (!coordsArr?.[0]) return;
    const ring = coordsArr[0];
    const first = ring[0];
    const last = ring[ring.length - 1];
    const isClosed =
      !!first && !!last && first[0] === last[0] && first[1] === last[1];
    const featureLevel = feature.properties?.[MapStoreyProperty.Level];
    const targetIndex =
      (featureLevel !== undefined
        ? options.levelInsertIndexes?.[featureLevel]
        : undefined) ??
      pointIndex ??
      0;
    ring[targetIndex] = [coords.lng, coords.lat];
    if (isClosed && targetIndex === 0) {
      ring[ring.length - 1] = [coords.lng, coords.lat];
    }
  });
  polySource?.setData(polyData);
};

/**
 * Set hover state for a point on the map.
 */
export const setHoveredPoint = (
  map: Map,
  id: string | number | null | undefined,
  currentHoveredId: string | number | null,
  sourceId: SourceId = SourceId.Points,
): string | number | null => {
  if (id == null) {
    return clearHoveredPoint(map, currentHoveredId, sourceId);
  }
  if (currentHoveredId !== null && currentHoveredId !== id) {
    map.setFeatureState(
      { source: sourceId, id: currentHoveredId },
      { hover: false },
    );
  }
  map.setFeatureState({ source: sourceId, id }, { hover: true });
  return id;
};

/**
 * Clear hover state for the currently hovered point.
 * // TODO: HOW??, points don't have ids?
 */
export const clearHoveredPoint = (
  map: Map,
  hoveredPointId: string | number | null,
  sourceId: SourceId = SourceId.Points,
): null => {
  if (hoveredPointId === null) return null;
  map.setFeatureState(
    { source: sourceId, id: hoveredPointId },
    { hover: false },
  );
  return null;
};

/**
 * Update point sources on the map.
 */
type PointRenderOptions = {
  suppressPoints?: boolean;
  suppressLines?: boolean;
  hasPointInsert?: boolean;
};

export const updatePointSources = (
  map: Map,
  storeys: Storey[],
  draftShape: DraftShape | undefined,
  selections: MapSelections,
  hoverState: MapHoverState | undefined,
  origin: Coordinate,
  options: PointRenderOptions = {},
): void => {
  renderPoints(
    map,
    storeys,
    draftShape,
    selections,
    hoverState,
    origin,
    options,
  );
  renderDraftPoints(
    map,
    storeys,
    draftShape,
    selections,
    hoverState,
    origin,
    options,
  );
};

/**
 * Render points on the map.
 * TODO: Move to map-renderer helper
 */
export const renderPoints = (
  map: Map,
  storeys: Storey[],
  draftShape: DraftShape | undefined,
  selections: MapSelections,
  hoverState: MapHoverState | undefined,
  origin: Coordinate,
  options: PointRenderOptions = {},
): void => {
  const draftActive = Boolean(draftShape?.points.length);
  const shouldShowPoints = !draftActive && !options.suppressPoints;
  setLayerVisibility(map, MapLayerIds.Points, shouldShowPoints);
  const pointsGeoJson = getMapStoreyPointsFromStoreys(
    storeys,
    selections,
    hoverState,
    undefined,
    origin,
    {
      includeInsertCandidate:
        Boolean(options.hasPointInsert) && shouldShowPoints,
    },
  );
  applyGeoJsonToSource(map, SourceId.Points, pointsGeoJson);
};

/**
 * Render draft points on the map.
 */
export const renderDraftPoints = (
  map: Map,
  storeys: Storey[],
  draftShape: DraftShape | undefined,
  selections: MapSelections,
  hoverState: MapHoverState | undefined,
  origin: Coordinate,
  options: PointRenderOptions = {},
): void => {
  if (options.suppressPoints) {
    applyGeoJsonToSource(map, SourceId.DraftPoints, EMPTY_POINT_GEOJSON);
    setLayerVisibility(map, MapLayerIds.DraftPoints, false);
    return;
  }
  if (!draftShape?.points.length) {
    applyGeoJsonToSource(map, SourceId.DraftPoints, EMPTY_POINT_GEOJSON);
    return;
  }
  const draftPointsGeoJson = getMapStoreyPointsFromStoreys(
    storeys,
    selections,
    hoverState,
    draftShape,
    origin,
  );
  applyGeoJsonToSource(map, SourceId.DraftPoints, draftPointsGeoJson);
};

export const updateLocationPinSource = (
  map: Map,
  location: Coordinate,
  visible: boolean,
): void => {
  if (!visible) {
    applyGeoJsonToSource(map, SourceId.LocationPin, EMPTY_LOCATION_PIN_GEOJSON);
    setLayerVisibility(map, MapLayerIds.LocationPin, false);
    return;
  }
  const geoJson: MapStoreyGeoJson<MapStoreyPointFeature> = {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          id: 'location-pin',
          properties: {
            [MapStoreyProperty.StoreyHeight]: 0,
            [MapStoreyProperty.Elevation]: 0,
            [MapStoreyProperty.Level]: 0,
            [MapStoreyProperty.Selected]: false,
            [MapStoreyProperty.Hover]: false,
            [MapStoreyProperty.Transparent]: false,
            [MapStoreyProperty.TopHeight]: 0,
            [MapStoreyProperty.Mezzanine]: false,
            index: 0,
          },
          geometry: {
            type: 'Point',
            coordinates: [location.lng, location.lat],
          },
        },
      ],
    },
  };
  applyGeoJsonToSource(map, SourceId.LocationPin, geoJson);
  setLayerVisibility(map, MapLayerIds.LocationPin, true);
};

const setLayerVisibility = (
  map: Map,
  layerId: MapLayerIds,
  visible: boolean,
): void => {
  const layerExists = map.getLayer(layerId);
  if (!layerExists) return;
  const nextVisibility = visible ? 'visible' : 'none';
  const currentVisibility = map.getLayoutProperty(layerId, 'visibility');
  if (currentVisibility === nextVisibility) return;
  map.setLayoutProperty(layerId, 'visibility', nextVisibility);
};

/**
 * Render building polygons on the map.
 */
export const renderBuildingPolygons = (
  map: Map,
  storeys: Storey[],
  selectedStoreyId: Storey['id'] | undefined,
  origin: Coordinate,
): ReturnType<typeof getMapStoreyPolygonsFromStoreys> => {
  const building = getMapStoreyPolygonsFromStoreys(
    storeys,
    selectedStoreyId,
    origin,
  );
  applyGeoJsonToSource(map, BUILDING_SOURCE_ID, building);
  return building;
};
